import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { format, addDays, addWeeks } from 'date-fns';
import { Phone, MessageCircle, Mail, MessageSquare, Users, Linkedin, MoreHorizontal, Calendar, Loader2, Repeat, XCircle } from 'lucide-react';
import FileUpload from './FileUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CHANNELS = [
    { value: 'call', label: 'Phone Call', icon: Phone, color: 'text-blue-600' },
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-green-600' },
    { value: 'email', label: 'Email', icon: Mail, color: 'text-purple-600' },
    { value: 'sms', label: 'SMS', icon: MessageSquare, color: 'text-orange-600' },
    { value: 'meeting', label: 'Meeting', icon: Users, color: 'text-red-600' },
    { value: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' },
    { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'text-slate-600' },
];

const OUTCOMES = [
    { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-800' },
    { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-800' },
    { value: 'no_answer', label: 'No Answer', color: 'bg-slate-100 text-slate-700' },
    { value: 'callback_requested', label: 'Callback Requested', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'follow_up_needed', label: 'Follow-up Needed', color: 'bg-blue-100 text-blue-800' },
    { value: 'deal_closed', label: 'Deal Closed', color: 'bg-emerald-100 text-emerald-800' },
];

const QUICK_FOLLOWUP_PRESETS = [
    { label: 'Tomorrow', getDays: () => 1 },
    { label: '3 Days', getDays: () => 3 },
    { label: '1 Week', getDays: () => 7 },
    { label: '2 Weeks', getDays: () => 14 },
    { label: '1 Month', getDays: () => 30 },
];

const LogInteractionModal = ({ open, onClose, leadId, leadName, onSaved, contextNote }) => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [cadences, setCadences] = useState([]);
    const [saving, setSaving] = useState(false);
    const interactionFileUploadRef = useRef(null);

    const [channel, setChannel] = useState('call');
    const [outcome, setOutcome] = useState('');
    const [notes, setNotes] = useState('');
    const [occurredAt, setOccurredAt] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    const [durationMinutes, setDurationMinutes] = useState('');

    const [scheduleFollowUp, setScheduleFollowUp] = useState(false);
    const [followUpDate, setFollowUpDate] = useState('');
    const [followUpAssigneeId, setFollowUpAssigneeId] = useState('');
    const [followUpDescription, setFollowUpDescription] = useState('');

    const [enrollCadence, setEnrollCadence] = useState(false);
    const [selectedCadenceId, setSelectedCadenceId] = useState('');
    const [activeEnrollments, setActiveEnrollments] = useState([]);
    const [cancelActiveCadences, setCancelActiveCadences] = useState(false);

    useEffect(() => {
        if (open) {
            fetchTeamMembers();
            fetchCadences();
            // Reset form
            setChannel('call');
            setOutcome('');
            setNotes('');
            setOccurredAt(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
            setDurationMinutes('');
            setScheduleFollowUp(false);
            setFollowUpDate('');
            setFollowUpAssigneeId('');
            setFollowUpDescription('');
            setEnrollCadence(false);
            setSelectedCadenceId('');
            setActiveEnrollments([]);
            setCancelActiveCadences(false);
        }
    }, [open]);

    const fetchTeamMembers = async () => {
        try {
            const res = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(res.data);
        } catch (e) {
            console.error('Failed to fetch team members', e);
        }
    };

    const fetchCadences = async () => {
        try {
            const [templatesRes, enrollRes] = await Promise.all([
                axios.get(`${API_URL}/cadences`),
                axios.get(`${API_URL}/leads/${leadId}/cadence-enrollments`),
            ]);
            setCadences(templatesRes.data);
            const active = enrollRes.data.filter(e => e.status === 'active');
            setActiveEnrollments(active);
        } catch (e) {
            console.error('Failed to fetch cadences', e);
        }
    };

    const POSITIVE_OUTCOMES = ['interested', 'deal_closed', 'callback_requested'];

    const handleOutcomeChange = (value) => {
        setOutcome(value);
        if (activeEnrollments.length > 0) {
            setCancelActiveCadences(POSITIVE_OUTCOMES.includes(value));
        }
    };

    const applyQuickPreset = (days) => {
        const date = addDays(new Date(), days);
        setFollowUpDate(format(date, 'yyyy-MM-dd'));
        setScheduleFollowUp(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!outcome) {
            toast.error('Please select an outcome');
            return;
        }
        if (scheduleFollowUp && !followUpDate) {
            toast.error('Please set a follow-up date');
            return;
        }

        try {
            setSaving(true);
            const occurredAtDate = new Date(occurredAt);
            if (isNaN(occurredAtDate.getTime())) {
                toast.error('Invalid date/time for "When". Please correct it and try again.');
                setSaving(false);
                return;
            }
            const payload = {
                channel,
                outcome,
                notes: notes.trim() || null,
                occurred_at: occurredAtDate.toISOString(),
                duration_minutes: durationMinutes ? parseInt(durationMinutes, 10) : null,
            };

            if (scheduleFollowUp && followUpDate) {
                const [year, month, day] = followUpDate.split('-').map(Number);
                const due = new Date(year, month - 1, day, 23, 59, 59);
                payload.next_follow_up_date = due.toISOString();
                if (followUpAssigneeId) payload.next_follow_up_assignee_id = followUpAssigneeId;
                if (followUpDescription.trim()) payload.next_follow_up_description = followUpDescription.trim();
            }

            const interactionRes = await axios.post(`${API_URL}/leads/${leadId}/interactions`, payload);
            const interactionId = interactionRes.data.id;
            let uploadFailed = false;
            if (interactionFileUploadRef.current?.hasPending()) {
                try {
                    await interactionFileUploadRef.current.uploadPending(leadId, 'interaction', interactionId);
                } catch (uploadErr) {
                    console.error('File upload failed after logging interaction', uploadErr);
                    uploadFailed = true;
                }
            }
            if (uploadFailed) {
                toast.warning('Interaction logged · file upload failed');
            } else {
                toast.success('Interaction logged' + (scheduleFollowUp ? ' · follow-up scheduled' : ''));
            }

            if (cancelActiveCadences && activeEnrollments.length > 0) {
                try {
                    await axios.delete(`${API_URL}/leads/${leadId}/cadences/active`);
                    toast.success('Remaining cadence steps cancelled');
                } catch {
                    toast.error('Failed to cancel cadence');
                }
            }

            if (enrollCadence && selectedCadenceId) {
                try {
                    await axios.post(`${API_URL}/leads/${leadId}/enroll-cadence`, {
                        cadence_id: selectedCadenceId,
                        start_date: new Date().toISOString(),
                    });
                    toast.success('Enrolled in cadence');
                } catch {
                    toast.error('Failed to enroll in cadence');
                }
            }

            onSaved && onSaved();
            onClose && onClose();
        } catch (err) {
            toast.error(getApiError(err, 'Failed to log interaction'));
        } finally {
            setSaving(false);
        }
    };

    const selectedChannel = CHANNELS.find(c => c.value === channel);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Log Interaction — {leadName}</DialogTitle>
                    {contextNote && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                            {contextNote} — what triggered this move?
                        </p>
                    )}
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 pt-1">
                    {/* Channel */}
                    <div className="space-y-2">
                        <Label>Channel</Label>
                        <div className="grid grid-cols-4 gap-2">
                            {CHANNELS.map(ch => {
                                const Icon = ch.icon;
                                return (
                                    <button
                                        key={ch.value}
                                        type="button"
                                        onClick={() => setChannel(ch.value)}
                                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs font-medium transition-all ${
                                            channel === ch.value
                                                ? 'border-slate-900 bg-slate-900 text-white'
                                                : 'border-slate-200 hover:border-slate-400 text-slate-600'
                                        }`}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {ch.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Outcome */}
                    <div className="space-y-2">
                        <Label>Outcome <span className="text-red-500">*</span></Label>
                        <div className="flex flex-wrap gap-2">
                            {OUTCOMES.map(o => (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => handleOutcomeChange(o.value)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                        outcome === o.value
                                            ? 'ring-2 ring-offset-1 ring-slate-900 ' + o.color
                                            : 'border-slate-200 text-slate-600 hover:border-slate-400'
                                    }`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date/time + duration */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label>When</Label>
                            <Input
                                type="datetime-local"
                                value={occurredAt}
                                onChange={e => setOccurredAt(e.target.value)}
                                className="text-sm"
                            />
                        </div>
                        {(channel === 'call' || channel === 'meeting') && (
                            <div className="space-y-1">
                                <Label>Duration (minutes)</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={durationMinutes}
                                    onChange={e => setDurationMinutes(e.target.value)}
                                    placeholder="e.g. 15"
                                    className="text-sm"
                                />
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div className="space-y-1">
                        <Label>Notes</Label>
                        <Textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="What was discussed? Any key points..."
                            className="min-h-[80px] resize-none text-sm"
                        />
                    </div>

                    {/* Attachments */}
                    <div className="space-y-1">
                        <Label>Attachments</Label>
                        <FileUpload
                            ref={interactionFileUploadRef}
                            leadId={leadId}
                            entityType="interaction"
                            uploadAfterCreate={true}
                            compact={true}
                        />
                    </div>

                    {/* Follow-up toggle */}
                    <div className="border-t pt-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2 cursor-pointer">
                                <Calendar className="h-4 w-4 text-slate-500" />
                                Schedule follow-up task?
                            </Label>
                            <Switch checked={scheduleFollowUp} onCheckedChange={setScheduleFollowUp} />
                        </div>

                        {scheduleFollowUp && (
                            <div className="space-y-3 pl-1">
                                {/* Quick presets */}
                                <div className="flex flex-wrap gap-1.5">
                                    {QUICK_FOLLOWUP_PRESETS.map(p => (
                                        <button
                                            key={p.label}
                                            type="button"
                                            onClick={() => applyQuickPreset(p.getDays())}
                                            className="px-2.5 py-1 text-xs rounded-md border border-slate-200 hover:bg-slate-100 text-slate-600 font-medium"
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label>Follow-up date <span className="text-red-500">*</span></Label>
                                        <Input
                                            type="date"
                                            value={followUpDate}
                                            onChange={e => setFollowUpDate(e.target.value)}
                                            min={format(new Date(), 'yyyy-MM-dd')}
                                            className="text-sm"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label>Assign to</Label>
                                        <Select value={followUpAssigneeId} onValueChange={value => setFollowUpAssigneeId(String(value))}>
                                            <SelectTrigger className="text-sm">
                                                <SelectValue placeholder="Me" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {teamMembers.map(m => (
                                                    <SelectItem key={String(m.id)} value={String(m.id)}>{m.email}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <Label>Task description</Label>
                                    <Input
                                        value={followUpDescription}
                                        onChange={e => setFollowUpDescription(e.target.value)}
                                        placeholder={`Follow up with ${leadName || 'lead'} via ${channel}`}
                                        className="text-sm"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Cancel active cadences — shown only if lead has active enrollments */}
                    {activeEnrollments.length > 0 && (
                        <div className="border-t pt-4">
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 cursor-pointer">
                                    <XCircle className="h-4 w-4 text-red-400" />
                                    <span>
                                        Cancel remaining cadence steps?
                                        <span className="ml-1.5 text-slate-400 font-normal">
                                            ({activeEnrollments.length} active · {activeEnrollments.reduce((n, e) => n + (Array.isArray(e.steps) ? e.steps : []).filter(s => s.status === 'pending').length, 0)} tasks)
                                        </span>
                                    </span>
                                </Label>
                                <Switch checked={cancelActiveCadences} onCheckedChange={setCancelActiveCadences} />
                            </div>
                            {cancelActiveCadences && (
                                <p className="text-xs text-red-500 mt-1.5 pl-6">
                                    All pending tasks from active cadences will be deleted.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Cadence enrollment */}
                    <div className="border-t pt-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2 cursor-pointer">
                                <Repeat className="h-4 w-4 text-violet-500" />
                                Enroll in a cadence?
                            </Label>
                            <Switch checked={enrollCadence} onCheckedChange={v => { setEnrollCadence(v); if (v) setScheduleFollowUp(false); }} />
                        </div>
                        {enrollCadence && (
                            <div className="pl-1 space-y-2">
                                <Select value={selectedCadenceId} onValueChange={setSelectedCadenceId}>
                                    <SelectTrigger className="text-sm">
                                        <SelectValue placeholder="Pick a cadence…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cadences.map(c => (
                                            <SelectItem key={c.id} value={c.id}>
                                                <span className="font-medium">{c.name}</span>
                                                <span className="ml-2 text-slate-400 text-xs">({c.steps.length} steps)</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {selectedCadenceId && (() => {
                                    const cad = cadences.find(c => c.id === selectedCadenceId);
                                    if (!cad) return null;
                                    return (
                                        <div className="flex flex-wrap gap-1.5">
                                            {Array.isArray(cad.steps) ? cad.steps.map(s => (
                                                <span key={s.step_number} className="text-xs bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-full">
                                                    D+{s.days_offset} · {s.channel}
                                                </span>
                                            )) : null}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {enrollCadence ? 'Log & Enroll in Cadence' : scheduleFollowUp ? 'Log & Schedule Follow-up' : 'Log Interaction'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default LogInteractionModal;
